import { useApi } from "@/hooks/useApi";
import { sceneStats as mockSceneStats } from "@/mocks/scenes";
import { routes as mockRoutes, routeStats as mockRouteStats, type Route } from "@/mocks/routes";

interface ModuleCard {
  title: string;
  desc: string;
  icon: string;
  path: string;
  iconBg: string;
  iconColor: string;
}

const modules: ModuleCard[] = [
  {
    title: "游线导览",
    desc: "按游线（含上行/下行方向）组织讲解，方向化讲解词独立维护",
    icon: "ri-route-line",
    path: "/knowledge/routes",
    iconBg: "bg-cyan-50",
    iconColor: "text-cyan-600",
  },
  {
    title: "点位内容",
    desc: "按分组管理沿线点位（位置、触发半径、知识库、默认讲解音频）",
    icon: "ri-map-2-line",
    path: "/knowledge/scenes",
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
  },
  {
    title: "讲解音频",
    desc: "TTS 讲解音频预生成、多角色（3 人格）× 多语种音轨管理",
    icon: "ri-volume-up-line",
    path: "/knowledge/audio",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    title: "预设话术",
    desc: "命名话术框 × 三人格 × 多段，每段独立 TTS 试听",
    icon: "ri-questionnaire-line",
    path: "/knowledge/global-qa",
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
  },
  {
    title: "视频内容",
    desc: "宣传片、纪录片等视频素材库",
    icon: "ri-film-line",
    path: "/videos",
    iconBg: "bg-rose-50",
    iconColor: "text-rose-600",
  },
  {
    title: "资源包发布",
    desc: "改完内容后点这里发版本 → 300 台手机增量更新",
    icon: "ri-rocket-2-line",
    path: "/knowledge/releases",
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
  },
];

function KnowledgeOverviewPage() {
  const { data: sceneStats } = useApi<typeof mockSceneStats>("/scenes/stats", mockSceneStats);
  const { data: routes } = useApi<Route[]>("/routes", mockRoutes as Route[]);
  const { data: routeStats } = useApi<typeof mockRouteStats>("/routes/stats", mockRouteStats);

  const stats = [
    { label: "游线总数", value: routeStats.totalRoutes, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
    { label: "已激活景点", value: sceneStats.activeScenes, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
    { label: "知识块总数", value: sceneStats.knowledgeCount, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
    { label: "讲解音频已生成", value: sceneStats.audioGenerated, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">三峡专属知识库 · 总览</h1>
        <p className="text-sm text-gray-500 mt-1">两坝一峡讲解内容、音频、知识图谱与视频素材的统一入口</p>
      </div>

      {/* 数据汇总 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* 模块入口 */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">模块入口</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {modules.map((m) => (
            <a
              key={m.path}
              href={m.path}
              className="group bg-white border border-gray-200 rounded-lg p-5 hover:border-teal-300 hover:shadow-md transition-all"
            >
              <div className={`w-11 h-11 ${m.iconBg} rounded-lg flex items-center justify-center mb-3`}>
                <i className={`${m.icon} text-xl ${m.iconColor}`} />
              </div>
              <div className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-teal-700">{m.title}</div>
              <div className="text-xs text-gray-500 leading-relaxed">{m.desc}</div>
              <div className="mt-3 text-xs text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                进入管理 <i className="ri-arrow-right-line" />
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* 游线速览 */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-900">游线速览</h2>
          <a href="/knowledge/routes" className="text-xs text-teal-600 hover:text-teal-700 font-medium">
            进入游线管理 <i className="ri-arrow-right-line" />
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {routes.map((r) => (
            <a
              key={r.id}
              href={`/knowledge/routes/${r.id}`}
              className="border border-gray-200 rounded-lg p-4 hover:border-teal-300 hover:bg-teal-50/20 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-gray-900">{r.name}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    r.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {r.status === "active" ? "已激活" : "草稿"}
                </span>
              </div>
              <div className="text-xs text-gray-500 mb-2 line-clamp-2">{r.description}</div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span><i className="ri-time-line mr-0.5" />{r.durationLabel}</span>
                <span><i className="ri-map-pin-line mr-0.5" />{r.sceneCount} 个点位</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default KnowledgeOverviewPage;
